import { useEffect, useRef, useState } from "react";
import axios from "axios";

// Streams this device's browser geolocation to `url` every 2s while enabled.
export function useLocationReporter(url, enabled, { wakeLock = false } = {}) {
  const [state, setState] = useState({ status: "idle", error: "", lastSent: null, fix: null });
  const fixRef = useRef(null);

  useEffect(() => {
    if (!enabled || !url) { setState((s) => ({ ...s, status: "idle" })); return undefined; }
    if (!("geolocation" in navigator)) { setState((s) => ({ ...s, status: "error", error: "Location services not supported on this device" })); return undefined; }
    let lock = null;
    setState((s) => ({ ...s, status: "acquiring", error: "" }));
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        fixRef.current = pos;
        setState((s) => ({ ...s, fix: pos.coords, status: s.status === "error" || s.status === "acquiring" ? "acquiring" : s.status, error: "" }));
      },
      (err) => setState((s) => ({ ...s, status: "error", error: err.code === 1 ? "Location permission denied — enable Location for this site in browser/OS settings" : err.message })),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );
    const send = () => {
      const pos = fixRef.current;
      if (!pos) return;
      const c = pos.coords;
      axios.post(url, {
        lat: c.latitude, lng: c.longitude, accuracy: c.accuracy,
        speed_mps: c.speed != null && c.speed >= 0 ? c.speed : undefined,
        heading: c.heading != null && !Number.isNaN(c.heading) ? c.heading : undefined,
        ts: pos.timestamp / 1000,
      }, { timeout: 5000 })
        .then(() => setState((s) => ({ ...s, status: "sending", lastSent: new Date(), error: "" })))
        .catch((e) => setState((s) => ({ ...s, status: "error", error: e?.response?.data?.detail || "Upload failed — retrying" })));
    };
    const timer = setInterval(send, 2000);
    const grabLock = async () => {
      try { if (wakeLock && navigator.wakeLock && document.visibilityState === "visible") lock = await navigator.wakeLock.request("screen"); } catch { /* unsupported */ }
    };
    grabLock();
    document.addEventListener("visibilitychange", grabLock);
    return () => {
      navigator.geolocation.clearWatch(watchId);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", grabLock);
      if (lock) lock.release().catch(() => {});
    };
  }, [url, enabled, wakeLock]);

  return state;
}

export function GpsShareIndicator({ state, inline = false }) {
  const color = state.status === "sending" ? "#00CC66" : state.status === "error" ? "#FF4444" : "#FFB000";
  const text = state.status === "sending" ? "GPS SHARING" : state.status === "error" ? "GPS ERROR" : "GPS ACQUIRING";
  return (
    <div data-testid="mdt-gps-indicator" title={state.error || (state.lastSent ? `Last sent ${state.lastSent.toLocaleTimeString()}` : "")}
      style={{ ...(inline ? {} : { position: "fixed", right: 8, bottom: 8, zIndex: 3000 }), display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,.8)", border: `1px solid ${color}`, borderRadius: 3, padding: "2px 8px", fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 700, color }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      {text}
    </div>
  );
}
